<template>
  <div class="expandable-textarea">
    <!-- Кнопка -->
    <button @click="toggle" type="button" class="toggle-btn">
        <div :class="{'active': isOpen}">
          <CirclePlus v-if="!isOpen"/>
          <CircleMinus v-else/>
        </div>
      {{ isOpen ? closeLabel : openLabel }}
    </button>

    <!-- Анимированный блок -->
    <transition name="collapse">
        <div v-show="isOpen" ref="contentRef" class="animated-box">
          <label v-if="label" :for="textareaId" class="label">{{ label }}</label>
          <textarea 
            :id="textareaId"
            :placeholder="placeholder"
            v-bind="inputProps"
            v-model="localValue"
            class="textarea"
          />
        </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CirclePlus, CircleMinus } from '@lucide/vue';

const { t } = useI18n();

const props = defineProps({
  label: String,
  placeholder: String,
  // Empty defaults resolve to the shared instruction copy below; callers may
  // override with their own labels.
  buttonOpenLabel: {
    type: String,
    default: '',
  },
  buttonCloseLabel: {
    type: String,
    default: '',
  },
  modelValue: String,
  inputProps: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['update:modelValue'])

const openLabel = computed(() => props.buttonOpenLabel || t('common.addInstructions'));
const closeLabel = computed(() => props.buttonCloseLabel || t('common.hideInstructions'));

// const isOpen = ref(false)
const isOpen = ref(!!props.modelValue?.trim())
const localValue = ref(props.modelValue ?? '')
const contentRef = ref(null)

const textareaId = computed(() =>
  props.inputProps?.id || `expand-textarea-${Math.random().toString(36).slice(2)}`
)

// Синхронизация
watch(
  () => props.modelValue,
  val => {
    if (val !== localValue.value) localValue.value = val
  }
)

watch(localValue, val => emit('update:modelValue', val))

const toggle = () => (isOpen.value = !isOpen.value)
</script>

<style scoped>
.expandable-textarea {
  margin-bottom: 1rem;
}

.toggle-btn {
    background: none;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    font-size: 1rem;
    margin-bottom: 0.5rem;
    padding: 0;
    font-weight: 500;
    letter-spacing: -0.25px;
    text-transform: initial;
    display: flex;
    justify-content: flex-start;
    align-content: center;
    gap: 0.375rem;

    div {
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-content: center;
        align-items: center;
        background: #ffffff;
        line-height: 0.77;
        /* outline: 1px solid var(--text-primary); */
        color: var(--text-primary);
        
        &.active {
            /* background: var(--text-primary);
            color: #fff; */
        }
    }
}

.animated-box {
  overflow: hidden;
  position: relative;
}

.label {
    color: var(--text-secondary);
    color: #727272;
    font-size: .8rem;
    left: 1px;
    line-height: 1;
    margin: 8px;
    padding: 0;
    position: absolute;
    top: 1px;
    transition: font-size .2s ease;
    font-weight: 500;
}

textarea {
    min-height: 5rem;
    width: 100%;
    line-height: 1.5;
    padding: 26px 8px 8px;
    border: 1px solid var(--text-secondary);
    border-radius: 0.375rem;
    outline: none;
    font-size: 1rem;
    resize: vertical;

    &::placeholder {
        font-size: 0.875rem;
    }

    &:focus,
    &:active {
        color: var(--text-primary);
    }
}
/* transition name = collapse */
.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 500px; /* или адаптивно: 80vh и т.д. */
  opacity: 1;
}
</style>
